import { createServiceDelegate, createChannelDelegate } from './delegate';
import { SequenceID } from './sequence';
import type { PortalService } from './service';

/* eslint-disable @typescript-eslint/no-explicit-any */
export enum PortalEventType {
    Ping = 'ping',
    Pong = 'pong',
    Request = 'request',
    Response = 'response',
    Channel = 'channel',
}

export const PORTAL = '__PORTAL__' as const;

export type PortalEventPayloadMap = {
    [PortalEventType.Ping]: void;
    [PortalEventType.Pong]: void;
    [PortalEventType.Request]: { seq: string; serviceName: string; apiName: string; args: any[] };
    [PortalEventType.Response]: { seq: string; error?: string; data: any };
    [PortalEventType.Channel]: { channelName: string; data: any };
};

export interface PortalEvent<Event extends PortalEventType = PortalEventType> {
    type: Event;
    payload: PortalEventPayloadMap[Event];
    $owner: typeof PORTAL;
}

export interface PortalTransport {
    open(): Promise<void>;
    close(): Promise<void>;
    send(payload: PortalEvent): void;
    receive<E extends PortalEventType>(eventType: E, handler: (event: PortalEvent<E>) => void): () => void;
}

export interface PortalChannel<T> {
    publish(data: T): void;
    subscribe(handler: (data: T) => void): () => void;
}

export interface IPortalClient<
    ServiceMap extends Record<string, any> = object,
    ChannelPayloadMap extends Record<string, any> = object,
> {
    register<K extends keyof ServiceMap & string>(serviceName: K, service: ServiceMap[K]): void;
    service<K extends keyof ServiceMap & string>(
        serviceName: K,
    ): Pick<ServiceMap[K], keyof PortalService<ServiceMap[K]>>;
    channel<K extends keyof ChannelPayloadMap & string>(channelName: K): PortalChannel<ChannelPayloadMap[K]>;
    dispose(): void;
}

export class PortalClient<
    ServiceMap extends Record<string, any> = object,
    ChannelPayloadMap extends Record<string, any> = object,
> implements IPortalClient<ServiceMap, ChannelPayloadMap>
{
    protected readyPromise: Promise<void>;
    protected seq = new SequenceID();
    protected services: Partial<ServiceMap> = {};

    public constructor(protected transport: PortalTransport) {
        transport.receive(PortalEventType.Ping, (event) => {
            if (event.$owner !== PORTAL) {
                return;
            }

            transport.send({
                type: PortalEventType.Pong,
                payload: undefined,
                $owner: PORTAL,
            });
        });

        this.open();
    }

    public register<K extends keyof ServiceMap & string>(serviceName: K, service: ServiceMap[K]) {
        this.services[serviceName] = service;

        this.transport.receive(PortalEventType.Request, (event) => {
            if (event.$owner !== PORTAL || event.payload.serviceName !== serviceName) {
                return;
            }

            const { apiName, seq } = event.payload;
            const response = (error?: string, data?: any) => {
                this.readyPromise.then(() => {
                    this.transport.send({
                        type: PortalEventType.Response,
                        payload: { seq, error, data },
                        $owner: PORTAL,
                    });
                });
            };

            const api = service[apiName];
            if (typeof api !== 'function') {
                response(`PortalService ${serviceName}::${apiName} is not a function`);
                return;
            }

            let promise: Promise<any> | undefined = undefined;
            try {
                promise = api.call(service, ...event.payload.args);
            } catch (err) {
                response(`PortalService ${serviceName}::${apiName} throws an error: ${formatError(err)}`);
                return;
            }

            if (typeof promise?.then !== 'function') {
                // throw new Error(`PortalService ${serviceName}::${apiName} is not a promise`);
                promise = Promise.resolve(promise);
            }

            promise
                .then((data: any) => response(undefined, data))
                .catch((err: unknown) => {
                    response(`PortalService ${serviceName}::${apiName} rejects an error: ${formatError(err)}`);
                });
        });
    }

    public service<K extends keyof ServiceMap & string>(
        serviceName: K,
    ): Pick<ServiceMap[K], keyof PortalService<ServiceMap[K]>> {
        const service = this.services[serviceName];
        if (!service) {
            this.services[serviceName] = createServiceDelegate<ServiceMap[K]>(
                serviceName,
                this.transport,
                this.seq,
                this.readyPromise,
            );
        }

        return this.services[serviceName] as ServiceMap[K];
    }

    public channel<K extends keyof ChannelPayloadMap & string>(channelName: K): PortalChannel<ChannelPayloadMap[K]> {
        return createChannelDelegate<ChannelPayloadMap[K]>(channelName, this.transport, this.readyPromise);
    }

    public dispose() {
        this.transport.close();
    }

    protected open() {
        const interval = setInterval(() => {
            this.transport.send({
                type: PortalEventType.Ping,
                payload: undefined,
                $owner: PORTAL,
            });
        }, 100);

        this.readyPromise = new Promise((resolve) => {
            const unReceive = this.transport.receive(PortalEventType.Pong, () => {
                unReceive();
                clearInterval(interval);
                resolve();
            });
        });
    }
}

export function formatError(err: unknown): string {
    if (err instanceof Error) {
        return err.stack ?? err.message ?? String(err);
    }

    return String(err);
}
